import React, { useState } from 'react';
import { Col, ColorPicker, Flex, Form, message, Row } from 'antd';
import { CreateTeamGroup, UpdateTeamGroup } from '../../apis/teamGroups';
import { useToken } from '../../common/helper';
import { useSpin } from '../../components/SpinContext.jsx';
import { CustomInput, CustomTitle } from '../../components';
import CustomButton from '../../components/CustomButton.jsx';
import './TeamForm.css';

export interface GroupData {
  id: string;
  name: string;
  color: string;
}

interface GroupFormProps {
  /** The group to edit; absent to add one. */
  groupData?: GroupData;
  onClose: () => void;
  onSaved: () => void;
}

interface FormValues {
  name: string;
}

/**
 * Add or edit a group: a name and a color. Teams join a group from their own
 * form; a group has no users, data or channels of its own.
 */
const GroupForm: React.FC<GroupFormProps> = ({
  groupData,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm<FormValues>();
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();
  const [color, setColor] = useState(groupData?.color || '#000000');
  const isEditing = !!groupData;

  const onFinish = async (values: FormValues) => {
    if (!token) {
      message.error('Sign in again before saving the group.');
      return;
    }
    const model = { name: values.name.trim(), color };
    showSpin();
    try {
      const response = groupData
        ? await UpdateTeamGroup(token, groupData.id, model)
        : await CreateTeamGroup(token, model);
      if (response?.data?.data?.is_success) {
        message.success(
          `Group ${isEditing ? 'updated' : 'added'} successfully!`
        );
        onSaved();
        onClose();
      } else {
        message.error(response?.data?.data?.message || 'Failed to save group');
      }
    } catch (error) {
      console.error('API Error:', error);
      message.error('Failed to save group');
    } finally {
      hideSpin();
    }
  };

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={onFinish}
      initialValues={{ name: groupData?.name ?? '' }}
      className='team-form'
    >
      <div className='drawer-title-header'>
        <CustomTitle
          text={isEditing ? 'Edit group' : 'Add group'}
          fontSize={20}
          fontWeight='700'
          lineHeight='32px'
          color='var(--text-primary)'
          marginBottom={0}
        />
      </div>

      <div className='custom-scrollbar team-form__body'>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Form.Item
              label='Name'
              name='name'
              className='custom-input form-input-style'
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: 'Please enter a group name',
                },
                { max: 50, message: 'Group name cannot exceed 50 characters' },
              ]}
            >
              <CustomInput placeholder='Enter group name' />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item className='custom-input form-input-style' label='Color'>
              <Flex gap={16} align='center'>
                <ColorPicker
                  value={color}
                  showText
                  format='hex'
                  onChange={(newColor) => setColor(newColor.toHexString())}
                />
                <div
                  className='team-form__color-swatch'
                  // eslint-disable-next-line react/forbid-dom-props -- the swatch shows the picked color
                  style={{ backgroundColor: color }}
                ></div>
              </Flex>
            </Form.Item>
          </Col>
        </Row>

        <div className='drawer-custom-footer'>
          <Row gutter={[0, 8]}>
            <CustomButton
              text='Save'
              bgColor='#000'
              htmlType='submit'
              color='#fff'
              fontSize='16px'
              fontWeight='600'
              boxShadow='none'
              height='48px'
              width='100%'
            />
            <CustomButton
              text='Cancel'
              bgColor='transparent'
              color='var(--text-primary)'
              fontSize='16px'
              fontWeight='600'
              boxShadow='none'
              height='auto'
              width='100%'
              padding={'11.5px 0px 12px 0px'}
              border='none'
              marginTop='0px'
              onClick={onClose}
            />
          </Row>
        </div>
      </div>
    </Form>
  );
};

export default GroupForm;
